/**
 * useSubmitFeedback — passthrough hook for FeedbackSubmitPort (033 US5, T040).
 *
 * Reads the `feedbackSubmit` port from PortsContext and returns a callback that
 * forwards to `port.submit(...)`.
 *
 * TOLERATES an absent slot, unlike `useDeleteAccount`. The feedback tab is
 * mounted by the shell on eligible member routes (`showsFloatingFeedback`),
 * so a composition without
 * the port (a focused test render, the CMS preview frame) would otherwise crash
 * unrelated surfaces. Absence yields `null`, and the modal keeps its submit
 * affordance hidden rather than offering a button that cannot persist — the
 * honest degrade (P7): no fake success, no silent drop.
 *
 * Mutation-only, mirroring `useDeleteAccount`: no Suspense read, stable
 * identity via `useMemo` keyed on the port.
 *
 * Owned by feature 033-data-ownership-and-lifecycle-foundations.
 * Contract: specs/033-.../contracts/my-data-api.md.
 */

import { useContext, useMemo } from 'react';
import { PortsContext } from '../providers/PortsContext.js';
import type { FeedbackSubmission } from './FeedbackSubmitPort.js';

export type SubmitFeedback = (submission: FeedbackSubmission) => Promise<void>;

/** The submit callback, or `null` when no port is wired (honest degrade). */
export function useSubmitFeedback(): SubmitFeedback | null {
  const ports = useContext(PortsContext);
  const port = ports?.feedbackSubmit;

  // react-compiler-exception: callback identity must stay stable per port so consumers can use it as an effect dependency
  return useMemo<SubmitFeedback | null>(
    () => (port === undefined ? null : (submission) => port.submit(submission)),
    [port],
  );
}
